import { subDays } from 'date-fns'

// Pre-composed view templates. buildTemplatePayload stamps widget ids + a default
// account override (space's first account) at instantiation, and computes an
// absolute previous-period compare window (the hook only honors absolute compare).
const overviewWidgets = [
    { type: 'account-scorecard', title: 'Scorecard', layout: { x: 0, y: 0, w: 12, h: 3 }, config: { metricKeys: ['spend', 'roas', 'purchases', 'ctr'], showCompare: true } },
    { type: 'chart-line', title: 'Performance trend', layout: { x: 0, y: 3, w: 12, h: 8 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['spend', 'roas'], daysSplit: 1 } },
    { type: 'account-breakdown', title: 'Demographics', layout: { x: 0, y: 11, w: 6, h: 9 }, config: { dimension: 'demographic', metricKeys: ['spend'], topN: 10, sortBy: 'spend' } },
    { type: 'account-breakdown', title: 'Top countries', layout: { x: 6, y: 11, w: 6, h: 9 }, config: { dimension: 'country', metricKeys: ['roas'], topN: 10, sortBy: 'roas' } },
    { type: 'account-campaigns', title: 'Campaigns', layout: { x: 0, y: 20, w: 12, h: 10 }, config: { metricKeys: ['spend', 'roas', 'purchases', 'ctr'], topN: 20, sortBy: 'spend', showAccountTotal: true } },
]

const videoHighlightsWidgets = [
    { type: 'account-scorecard', title: 'Video metrics', layout: { x: 0, y: 0, w: 12, h: 3 }, config: { metricKeys: ['video_plays', 'hook_rate', 'hold_rate', 'thruplay', 'video_avg_time_watched'], showCompare: true } },
    { type: 'creative-highlights', title: 'Top video ads', layout: { x: 0, y: 3, w: 12, h: 7 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['hook_rate', 'hold_rate', 'thruplay'] } },
    { type: 'chart-comparison', title: 'Video metrics by creative', layout: { x: 0, y: 10, w: 12, h: 10 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['spend', 'hook_rate', 'hold_rate', 'thruplay', 'video_avg_time_watched'], displayCount: 6 } },
    { type: 'chart-bar', title: 'Impressions & hook rate', layout: { x: 0, y: 20, w: 12, h: 9 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['impressions', 'hook_rate'] } },
]

const creativePerformanceWidgets = [
    { type: 'chart-filmstrip', title: 'Top creatives', layout: { x: 0, y: 0, w: 12, h: 7 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['spend', 'roas'] } },
    { type: 'chart-bar', title: 'Top creatives by ROAS', layout: { x: 0, y: 7, w: 6, h: 9 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['roas'] } },
    { type: 'chart-bar', title: 'Top creatives by spend', layout: { x: 6, y: 7, w: 6, h: 9 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['spend'] } },
    { type: 'metric-table', title: 'All ads', layout: { x: 0, y: 16, w: 12, h: 10 }, config: { groupBy: { key: 'ad_name', label: 'Ad Name' }, metricKeys: ['spend', 'roas', 'purchases', 'ctr', 'impressions'] } },
]

const executiveSummaryWidgets = [
    { type: 'ai-summary', title: 'Executive summary', layout: { x: 0, y: 0, w: 12, h: 10 }, config: { promptKey: 'executive-summary' } },
    { type: 'account-scorecard', title: 'Scorecard', layout: { x: 0, y: 10, w: 12, h: 3 }, config: { metricKeys: ['spend', 'roas', 'purchases', 'ctr'], showCompare: true } },
    { type: 'chart-line', title: 'Performance trend', layout: { x: 0, y: 13, w: 12, h: 8 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['spend', 'roas'], daysSplit: 1 } },
]

const audiencePlacementWidgets = [
    { type: 'account-scorecard', title: 'Scorecard', layout: { x: 0, y: 0, w: 12, h: 3 }, config: { metricKeys: ['spend', 'roas', 'purchases', 'ctr'], showCompare: true } },
    { type: 'account-breakdown', title: 'Demographics', layout: { x: 0, y: 3, w: 6, h: 9 }, config: { dimension: 'demographic', metricKeys: ['spend'], topN: 10, sortBy: 'spend' } },
    { type: 'account-breakdown', title: 'Platform', layout: { x: 6, y: 3, w: 6, h: 9 }, config: { dimension: 'platform', metricKeys: ['spend'], topN: 10, sortBy: 'spend' } },
    { type: 'account-breakdown', title: 'Placement', layout: { x: 0, y: 12, w: 6, h: 9 }, config: { dimension: 'placement', metricKeys: ['roas'], topN: 10, sortBy: 'spend' } },
    { type: 'account-breakdown', title: 'Top countries', layout: { x: 6, y: 12, w: 6, h: 9 }, config: { dimension: 'country', metricKeys: ['roas'], topN: 10, sortBy: 'roas' } },
]

const spendEfficiencyWidgets = [
    { type: 'kpi-tile', title: 'Spend', layout: { x: 0, y: 0, w: 3, h: 2 }, config: { metricKey: 'spend', showSparkline: true, showCompare: true } },
    { type: 'kpi-tile', title: 'ROAS', layout: { x: 3, y: 0, w: 3, h: 2 }, config: { metricKey: 'roas', showSparkline: true, showCompare: true } },
    { type: 'kpi-tile', title: 'CTR', layout: { x: 6, y: 0, w: 3, h: 2 }, config: { metricKey: 'ctr', showSparkline: true, showCompare: true } },
    { type: 'kpi-tile', title: 'Purchases', layout: { x: 9, y: 0, w: 3, h: 2 }, config: { metricKey: 'purchases', showSparkline: true, showCompare: true } },
    { type: 'chart-line', title: 'Spend vs ROAS', layout: { x: 0, y: 2, w: 12, h: 8 }, config: { groupBy: { key: 'ad_creative', label: 'Creative' }, metricKeys: ['spend', 'roas'], daysSplit: 1 } },
    { type: 'account-campaigns', title: 'Campaigns', layout: { x: 0, y: 10, w: 12, h: 10 }, config: { metricKeys: ['spend', 'roas', 'purchases', 'ctr'], topN: 20, sortBy: 'spend', showAccountTotal: true } },
]

export const VIEW_TEMPLATES = [
    {
        key: 'overview',
        title: 'Overview',
        description: 'Account performance at a glance — scorecard, trend, segments, and campaigns.',
        icon: { value: 'ph:chart-pie-slice', color: 'text-primary' },
        widgets: overviewWidgets,
    },
    {
        key: 'video-highlights',
        title: 'Video Highlights',
        description: 'Top-performing video ads with watch-retention at a glance.',
        icon: { value: 'mdi:star-outline', color: 'text-primary' },
        widgets: videoHighlightsWidgets,
    },
    {
        key: 'creative-performance',
        title: 'Creative Performance',
        description: 'Which creatives win — top ads, ROAS & spend leaders, and a full ad table.',
        icon: { value: 'lucide:images', color: 'text-primary' },
        widgets: creativePerformanceWidgets,
    },
    {
        key: 'executive-summary',
        title: 'Executive Summary',
        description: 'An AI-written read of the period, a headline scorecard, and the performance trend.',
        icon: { value: 'lucide:sparkles', color: 'text-primary' },
        widgets: executiveSummaryWidgets,
    },
    {
        key: 'audience-placement',
        title: 'Audience & Placement',
        description: 'Where spend goes and who converts — demographics, platform, placement, country.',
        icon: { value: 'lucide:users', color: 'text-primary' },
        widgets: audiencePlacementWidgets,
    },
    {
        key: 'spend-efficiency',
        title: 'Spend & Efficiency',
        description: 'KPI tiles, a spend-vs-ROAS trend, and a campaigns table for efficiency monitoring.',
        icon: { value: 'lucide:gauge', color: 'text-primary' },
        widgets: spendEfficiencyWidgets,
    },
]

const newWidgetId = () => `w_${Math.random().toString(36).slice(2, 10)}`

// Build a createView payload from a template. defaultOverride = { adAccountIds, integrationIds }
// resolved from the space's first ad account (or null).
export const buildTemplatePayload = (template, defaultOverride) => {
    const to = new Date()
    const from = subDays(to, 30)
    const compareTo = subDays(from, 1)
    const compareFrom = subDays(compareTo, 29)
    return {
        title: template.title,
        description: template.description,
        icon: template.icon,
        scope: {
            dateRange: { mode: 'preset', preset: 'last_30d' },
            compareDates: { enabled: true, mode: 'absolute', from: compareFrom, to: compareTo },
            filters: [],
            adAccountIds: defaultOverride?.adAccountIds || [],
            integrationIds: defaultOverride?.integrationIds || [],
        },
        widgets: template.widgets.map(w => ({
            ...w,
            id: newWidgetId(),
            override: defaultOverride || null,
        })),
    }
}
